import {
  CAPTAIN_OPTION_1_INCLUDED_IN_PRICE, CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN, TIME_12, TIME_24,
} from 'src/common/constants';
import { convertToTimeZone, formatDate } from 'src/common/utils/dateHelpers';
import { InquiryData } from 'src/inquiry/types';
import {
  DepartureTime, TripCartDuration, TripCartFormState,
  TripCartMultiDayPrice,
} from 'src/listing/types';
import { TimeOfDay } from 'src/admin/pricing/types/TimeOfDay';
import { timeStringToDate, timeStringToDateUpdate } from 'src/common/helpers';
import { addHours, eachMinuteOfInterval, isBefore } from 'date-fns';
import { DurationUnit } from 'src/common/types/api/listings';
import { formatDurationUnit, TripCartDurationOption } from './utils/tripCartUtils';

const INTERVAL_MINUTES = 15;

// eslint-disable-next-line import/prefer-default-export
export const mapFormStateToInquiryData = (state: TripCartFormState): InquiryData => {
  const {
    formSelectedDates,
    formDuration,
    formGuests,
    withCaptain,
    message,
    departureTime,
  } = state;
  const captainValue = Number(withCaptain);

  /**
   * TODO: Need to replace the hardcoded values with the actual values from the form
   */
  const mappedInquiry: InquiryData = {
    pickup_time: departureTime,
    preferred_date1: formatDate(formSelectedDates[0]),
    adults: formGuests,
    trip_length: formDuration,
    comment: message,
    captain_required: captainValue === CAPTAIN_OPTION_1_INCLUDED_IN_PRICE
    || captainValue === CAPTAIN_OPTION_2_INDEPENDENT_CAPTAIN,
  };

  return mappedInquiry;
};

export const getDurationFromPriceSets = (
  priceSets: TripCartDuration[],
): TripCartDurationOption[] => priceSets.sort((a, b) => a.duration - b.duration)
  .map((priceSet) => ({
    duration: priceSet.duration,
    unit: 'hour',
    isInstabook: false,
    durationFormatted: formatDurationUnit(priceSet.duration, 'hour'),
  }));

export const getDurationFromMultiDayPrices = (
  multiDays: TripCartMultiDayPrice[],
  unit: DurationUnit,
): TripCartDurationOption[] => multiDays.flatMap(
  (multiDay) => Array.from(
    { length: multiDay.maxDuration - multiDay.minDuration + 1 },
    (_, i) => (multiDay.minDuration + i),
  ),
).map((duration) => ({
  duration,
  unit,
  isInstabook: false,
  durationFormatted: formatDurationUnit(duration, unit),
})).sort((a, b) => a.duration - b.duration);

export const getDepartureTimesForDuration = (
  priceSets: TripCartDuration[],
  duration: number,
): TimeOfDay[] => priceSets.find(
  (priceSet) => priceSet.duration === duration,
)?.departureTimes || [];

type GetDepartureTimesFromOperatingHoursParams = {
  operatingHoursDeparture: TimeOfDay;
  operatingHoursReturn: TimeOfDay;
  selectedDate: Date;
  locale: Locale;
  listingTimeZone: string | undefined;
  currentDate?: Date;
};

export const getDepartureTimesFromOperatingHours = ({
  operatingHoursDeparture,
  operatingHoursReturn,
  selectedDate,
  locale,
  listingTimeZone,
  currentDate = new Date(),
}: GetDepartureTimesFromOperatingHoursParams): DepartureTime[] => {
  const MIN_HOURS_BEFORE_INQUIRY = 1;
  // TODO: check min hour requirement
  const currentDateTime = listingTimeZone ? convertToTimeZone(
    addHours(currentDate, MIN_HOURS_BEFORE_INQUIRY),
    listingTimeZone,
  ) : null;

  const start = timeStringToDate(`${operatingHoursDeparture.hours}:${operatingHoursDeparture.minutes}`, selectedDate);
  const end = timeStringToDate(`${operatingHoursReturn.hours}:${operatingHoursReturn.minutes}`, selectedDate);
  return eachMinuteOfInterval({
    start,
    end,
  }, { step: INTERVAL_MINUTES })
    .map((dateTime) => ({
      formattedTime24: formatDate(dateTime, TIME_24, locale),
      formattedTime12: formatDate(dateTime, TIME_12, locale),
      dateTime,
    })).filter(({ dateTime }) => {
      if (!currentDateTime) return true;
      return isBefore(currentDateTime, dateTime);
    });
};

export const getTripCartDepartureTimes = (
  departureTimes: TimeOfDay[],
  locale: Locale,
): DepartureTime[] => departureTimes.map((time) => {
  const timeString = `${time.hours}:${time.minutes}`;
  const dateTime = timeStringToDateUpdate(timeString);
  return {
    formattedTime24: formatDate(dateTime, TIME_24, locale),
    formattedTime12: formatDate(dateTime, TIME_12, locale),
    dateTime,
  };
});

/**
 * Format operating hours string to TimeOfDay object
 * @param operatingHours - Operating hours string in HH:MM:SS format
 * @returns TimeOfDay object
 */
export const formatToTimeOfDay = (operatingHours: string): TimeOfDay => {
  const [hours, minutes] = operatingHours.split(':').map(Number);
  return { hours, minutes };
};
